import React from "react";
import { Link, SvgIcon } from "@mui/material";
import {
  Badge as BadgeIcon,
  ContactMail,
  Devices,
  DirectionsCar,
  EvStation,
  GroupAdd,
  Laptop,
  LocalParking,
  Person,
  Restaurant,
  RoomService
} from "@mui/icons-material";
import { BookingScheduleInterface } from "../../features/Booking-Form/typings/booking.types";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { Colleague } from "../../features/Connections/types/Colleague.type";
import { scheduleConferenceIcon } from "./schedule-event-content-icon.component.partial";
import { resolveScheduleResourceKind } from "../../features/Schedule/utils/resource-booking-schedule.utils";
import { shouldShowVisitorScheduleIcon } from "./should-show-visitor-schedule-icon.function";

type P = {
  schedule: BookingScheduleInterface;
  userId: string;
  textColor?: "black" | "white";
  iconSize?: number | string;
  bookingUser?: Colleague;
  /** When false (resource calendar / resource selection UI), hide conference visitor mail link. */
  showConferenceVisitorContactLink?: boolean;
};

export const ScheduleEventContentIcon: React.FC<P> = ({
  schedule,
  userId,
  textColor,
  iconSize = 18,
  bookingUser,
  showConferenceVisitorContactLink = true
}) => {
  const getDefaultPlaceIcon = () => {
    if (schedule.locationName === "Mobile Working") {
      return <Laptop sx={{ width: iconSize, height: iconSize, color: textColor }} />;
    } else {
      return <GroupAdd sx={{ width: iconSize, height: iconSize, color: textColor }} />;
    }
  };
  const getPlaceIcon = () => {
    switch (schedule.bookingType) {
      case BookingType.WORKPLACE: {
        if (!schedule.externalEmail && !schedule.externalName && !schedule.externalCompany) {
          return <Person sx={{ width: iconSize, height: iconSize, color: textColor }} />;
        }
        return getDefaultPlaceIcon();
      }
      case BookingType.CANTEENPLACE:
        return <Restaurant sx={{ width: iconSize, height: iconSize, color: textColor }} />;
      case BookingType.PARKINGPLACE:
        return <LocalParking sx={{ width: iconSize, height: iconSize, color: textColor }} />;
      case BookingType.ELECTRICCHARGINGSTATIONPLACE:
        return <EvStation sx={{ width: iconSize, height: iconSize, color: textColor }} />;
      case BookingType.VEHICLE:
        return <DirectionsCar sx={{ width: iconSize, height: iconSize, color: textColor }} />;
      case BookingType.RESOURCE: {
        const resourceKind = resolveScheduleResourceKind(schedule);
        if (resourceKind === "catering") {
          return <RoomService sx={{ width: iconSize, height: iconSize, color: textColor }} />;
        }
        return <Devices sx={{ width: iconSize, height: iconSize, color: textColor }} />;
      }
      default: {
        return getDefaultPlaceIcon();
      }
    }
  };

  const emailHref = (e: string | undefined) => {
    if (!e) return;
    return "mailto:" + e;
  };

  const nonConferenceIcon = shouldShowVisitorScheduleIcon(schedule) ? (
    <BadgeIcon sx={{ width: iconSize, height: iconSize, color: textColor }} />
  ) : (
    getPlaceIcon()
  );

  return (
    <>
      {schedule.bookingType !== BookingType.CONFERENCEZONE && <>{nonConferenceIcon}</>}

      {schedule.bookingType === BookingType.CONFERENCEZONE && (
        <SvgIcon
          data-testid="conference-icon"
          inheritViewBox
          sx={{
            width: iconSize,
            height: iconSize,
            flexShrink: 0
          }}
        >
          {scheduleConferenceIcon({ textColor })}
        </SvgIcon>
      )}

      {/* when user is visitor, connect email to booking users */}
      {showConferenceVisitorContactLink &&
        schedule.bookingType === BookingType.CONFERENCEZONE &&
        schedule.userBookedFor !== userId && (
          <Link
            href={emailHref(bookingUser?.email)}
            underline="none"
            sx={{
              color: "currentcolor",
              display: "inline-flex",
              alignItems: "center",
              lineHeight: 0,
              height: iconSize,
              flexShrink: 0,
              pr: 0.5
            }}
          >
            <ContactMail
              sx={{
                width: iconSize,
                height: iconSize,
                color: textColor,
                display: "block"
              }}
            />
          </Link>
        )}
    </>
  );
};
